import { Toaster } from "react-hot-toast";
import { BrowserRouter as Router } from "react-router-dom";
import * as Sentry from "@sentry/react";
import AppRoutes from "./routes/AppRoutes";
import { BusinessSettingsProvider } from "./contexts/BusinessSettingsContext";
import { UserProvider } from "./contexts/UserInfoContext";
import { createContext, useContext, useEffect, useState } from "react";
import OneSignal from "react-onesignal";

interface OneSignalContextType {
  isInitialized: boolean;
}

const OneSignalContext = createContext<OneSignalContextType>({
  isInitialized: false,
});

export const useOneSignal = () => useContext(OneSignalContext);

function App() {
  const [oneSignalInitialized, setOneSignalInitialized] = useState(false);

useEffect(() => {
  const initOneSignal = async () => {
    const appId = import.meta.env.VITE_ONESIGNAL_APP_ID;

    if (!appId) {
      console.warn("⚠️ Skipping OneSignal: VITE_ONESIGNAL_APP_ID not set in env.");
      setOneSignalInitialized(true);
      return;
    }

    try {
      console.log(`🔔 Initializing OneSignal with App ID: ${appId}`);

      await OneSignal.init({
        appId,
        allowLocalhostAsSecureOrigin: true,
      });

      // Listen for subscription state changes (opt-in/out, fresh subscribe).
      // Push Subscribers tab listens independently for its own UI updates.
      OneSignal.User.PushSubscription.addEventListener("change", (sub) => {
        console.log("📡 Push subscription changed:", sub);
      });

      // NOTE: we deliberately DO NOT auto-prompt for permission here. The
      // permission request now happens only when the user clicks "Enable
      // push for this browser" on /notifications/manage?tab=subscribers.
      // Auto-prompting on every app load is intrusive and breaks Chrome's
      // engagement requirements for the persistent prompt.
      const permission = (OneSignal as any).Notifications?.permission;
      console.log("🔐 Notification permission:", permission);

      const pushSub = OneSignal.User.PushSubscription;
      if (pushSub?.id) {
        console.log("✅ OneSignal subscription active:", pushSub.id);
      }

      setOneSignalInitialized(true);
    } catch (error) {
      console.error("❌ OneSignal initialization failed:", error);
      setOneSignalInitialized(true);
    }
  };

  initOneSignal();
}, []);



  return (
    <Router>
      <BusinessSettingsProvider>
        <UserProvider>
          <OneSignalContext.Provider
            value={{ isInitialized: oneSignalInitialized }}
          >
            <Sentry.ErrorBoundary
              fallback={({ error, resetError }) => (
                <div style={{ padding: 40, textAlign: "center" }}>
                  <h2>Something went wrong</h2>
                  <p style={{ color: "#666" }}>{error?.toString()}</p>
                  <button
                    onClick={resetError}
                    style={{ marginTop: 16, padding: "8px 20px", cursor: "pointer" }}
                  >
                    Try again
                  </button>
                </div>
              )}
              showDialog
            >
            <div className="App">
              <Toaster position="top-right" />
              <AppRoutes />
            </div>
            </Sentry.ErrorBoundary>
          </OneSignalContext.Provider>
        </UserProvider>
      </BusinessSettingsProvider>
    </Router>
  );
}

export default App;
